import { css } from '@emotion/css'
import colors from '@styles/colors';
import { useNotificationModal } from '@components/common/presentational/modal/common';
import Clickable from '@v2/components/common/presentational/clickables/default';
import { categoryMap } from '@components/common/products/utils';

const container = css({
    width: 120,
    height: 35,
    position: 'relative',
    fontSize: 14,
    margin: '0 auto',

    'input, input:disabled': {
        opacity: 1,
        height: 35,
        width: '100%',
        top: 0,
        left: 0,
        margin: 0,
        textAlign: 'center',
        position: 'absolute',
        zIndex: 1,
        border: `2px solid ${colors.BLUE_EXTRA_LIGHT}`,
        backgroundColor: colors.WHITE,
        borderRadius: 5,
        color: colors.NAVY,
        WebkitTextFillColor: colors.NAVY,
        outline: 'none',
        boxSizing: 'border-box', //mobile safari OS bug fixing
        WebkitAppearance: 'none', //mobile safari OS bug fixing
        MozAppearance: 'textfield', //FireFox fix to center the values exactly in the middle of the input field

        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            MozAppearance: 'textfield',
            margin: 0,
        },

        '&:active': {
            fontSize: 16,
        }
    },

    button: {
        position: 'absolute',
        zIndex: 2,
        textAlign: 'center',
        border: 'solid 2px',
        borderColor: colors.BLUE_EXTRA_LIGHT,
        backgroundColor: colors.WHITE,
        height: '100%',
        width: 35,
        lineHeight: '34px',
        fontSize: 25,
        color: colors.NAVY,
        cursor: 'pointer',
        boxSizing: 'border-box', //mobile safari OS bug fixing

        span: {
            position: 'relative',
            top: '-4px',
        },

        input: {
            outline: 'none',
        },

        '&.remove': {
            top: 0,
            left: 0,
            borderTopLeftRadius: 5,
            borderBottomLeftRadius: 5,
            padding: 0,
        },

        '&.add': {
            top: 0,
            right: 0,
            borderTopRightRadius: 5,
            borderBottomRightRadius: 5,
            padding: 0,
        },

        '&.disabled, &:disabled': {
            color: colors.BLACK_LIGHT_65,
            backgroundColor: colors.GRAY,
        },

        '&:focus, &:active': {
            outline: 0,
        },

        '&:hover': {
            backgroundColor: colors.BLUE_EXTRA_LIGHT,

            '&.disabled': {
                backgroundColor: colors.GRAY,
            },
        },
    },

    '&.medium': {
        height: 35,
        width: 120,

        input: {
            height: 35,
            fontWeight: 600,
            fontSize: 15,
            textAlign: 'center',
            letterSpacing: '0.01em',
            borderRadius: 2,
            lineHeight: '19px',
        },

        button: {
            width: 35,

            span: {
                position: 'relative',
                top: '-5px',
            },

            '&.remove': {
                borderTopLeftRadius: 2,
                borderBottomLeftRadius: 2,
            },

            '&.add': {
                borderTopRightRadius: 2,
                borderBottomRightRadius: 2,
            },
        },
    },

    //mobile safari OS zoom bug fixing
    '.input-overlay': {
        height: 35,
        width: '100%',
        top: 0,
        left: 0,
        margin: 0,
        position: 'absolute',
        zIndex: 1,
    },

    '.not-added-container': {
        display: 'none',
        textAlign: 'center',
        paddingTop: 4,

        '&.active': {
            span: {
                cursor: 'pointer',

                circle: {
                    transition: 'fill 0.2s ease',
                },

                '&:hover': {
                    circle: {
                        fill: colors.NAVY,
                    }
                },
            },
        },
    },

    '&.not-added': {
        '.added-container': {
            display: 'none',
        },

        '.not-added-container': {
            display: 'block',
        },
    },

    'button > span.notification': {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
    }
});

interface IQuantityInputProps {
    count: number;
    setCount: Function;
    isMaxed: boolean;
    isOutOfStock: boolean;
    title: string;
    size?: string;
    maxedType?: string;
    isButtonText?: boolean;
    onAddAction?: Function;
    boxType?: string;
    showUpgradeBoxModal?: Function;
}

export default function QuantityInput(
    { count, setCount, isMaxed, isOutOfStock, title, size, maxedType, isButtonText, onAddAction, boxType, showUpgradeBoxModal }
    : IQuantityInputProps) {
    const sizeClassName = size === 'medium' ? size : 'default';

    const [showNotificationModal] = useNotificationModal({
        message: `You've reached the maximum allowable ${categoryMap(maxedType)} for one meal plan.
            Don't worry! If you need more, click "Add additional plan" after you add this one to
            your cart and build another box. Plans can be rescheduled, paused or cancelled at any time.`,
        readonly: true,
    });

    const setCountAction = (count: number) => {
        setCount(count);
    }

    const addAction = () => {
        if (isMaxed === false) {
            setCountAction(1);
        } else if (maxedType) {
            showNotificationModal();
        } else if (isMaxed && showUpgradeBoxModal) {
            showUpgradeBoxModal();
        }
    }

    return (
        <div className={`${container} ${sizeClassName} ${count === 0 ? 'not-added' : 'added'}`}>
            <div className="added-container">
                <button
                    tabIndex={0}
                    aria-label={`Remove 1 ${title} From Cart`}
                    className="remove"
                    disabled={(boxType === 'product' || boxType === 'variety_pack') && count < 2}
                    onClick={e => setCountAction(Math.max(count - 1, 0))}>
                    <span>-</span>
                </button>
                <input
                    aria-hidden="true"
                    type="number"
                    aria-label={`${title} (${count} Quantity) In Cart`}
                    value={count}
                    readOnly={true}
                    disabled={true}
                    onChange={e => setCountAction(Number(e.target.value))} />
                <span
                    tabIndex={0}
                    aria-label={`${title} (${count} Quantity) In Cart`}
                    className="input-overlay">
                </span>
                <button
                    tabIndex={0}
                    aria-label={`Add 1 ${title} To Cart`}
                    className={`add ${isMaxed ? 'disabled' : ''}`}
                    onClick={e => {
                        if (!isMaxed) {
                            setCountAction(count + 1)
                        }
                    }}>
                    <span>+</span>
                    <span
                        className="notification on-add-line"
                        onClick={() => {
                            if (maxedType) {
                                showNotificationModal();
                            }
                            if (typeof onAddAction === 'function') {
                                onAddAction();
                            }
                            if (isMaxed && showUpgradeBoxModal) {
                                showUpgradeBoxModal();
                            }
                        }}>
                    </span>
                </button>
            </div>
            {!isOutOfStock &&
                <div className={`not-added-container ${isMaxed === false ? 'active' : ''}`}>
                        {!isButtonText
                            ?
                            <Clickable ariaLabel={`Add 1 ${title} To Cart`} onClick={addAction}>
                                <svg
                                    aria-hidden="true"
                                    width="30"
                                    height="30"
                                    viewBox="0 0 30 30"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <circle
                                        cx="15"
                                        cy="15"
                                        r="15"
                                        fill={isMaxed === false ? colors.BLUE : colors.GRAY} />
                                    <path
                                        d="M9.66663 15H20.334"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round" />
                                    <path
                                        d="M15.0005 20.3333V9.66669"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round" />
                                </svg>
                            </Clickable>
                            :
                            <button onClick={addAction} className={`button blue ${isMaxed === false ? '' : 'inactive'}`}>
                                <span>Add to box</span>
                            </button>
                        }
                </div>
            }

        </div>
    );
}
